import type { Ssm5DiagramModule } from '@packages/domain/ssm5';
import type { KeyboardEvent, MouseEvent, SyntheticEvent } from 'react';
import type { OverlayInjectedProps } from 'react-bootstrap/Overlay';

import { colors } from '@packages/core/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import {
    isSelectableStatus,
    STATUS_LABEL_KEY,
    STATUS_LOOKS,
    statusByModuleId,
    UNREPORTED_STATUS,
} from '../helpers/diagram-modules';
import { detachDiagramTitles, findDiagramCells, sanitizeDiagramSvg, toDiagramCell } from '../helpers/diagram-svg';

/** Props for {@link Ssm5CanBusDiagram}. */
export type Ssm5CanBusDiagramProps = {
    /** The modules the backend reported; a cell of none of them is not equipped. */
    modules: readonly Ssm5DiagramModule[];
    /** Called with the id of the module whose cell was selected. */
    onSelect: (moduleId: string) => void;
    /** The id of the module whose cell shows selected, if any. */
    selectedModuleId?: string;
    /** The diagram, as SVG text. */
    svg: string;
};

/** The tooltip over a cell: the cell it points at and the full name it shows. */
type DiagramTooltip = {
    /** The cell's group. */
    target: Element;
    /** The full name of the module. */
    text: string;
};

const SELECTED_SHADOW = 'drop-shadow(0 1.72px 4.29px rgba(0, 0, 0, 0.09))';

const SELECT_KEYS: ReadonlySet<string> = new Set(['Enter', ' ']);

const TOOLTIP_ID = 'ssm5-diagram-tooltip';

/**
 * The style rules that paint the cells by the attributes the page sets on them.
 *
 * @returns the nested rules of the diagram wrapper
 */
function cellRules(): Record<string, Record<string, string>> {
    const rules: Record<string, Record<string, string>> = {};

    for (const [status, { cell, selected }] of Object.entries(STATUS_LOOKS)) {
        rules[`& .can-module[data-status="${status}"] rect`] = { fill: cell.fill, stroke: cell.stroke };

        if (selected === undefined) {
            continue;
        }

        const selectedCell = `& .can-module[data-status="${status}"][data-selected="true"]`;

        rules[`${selectedCell} rect`] = { fill: selected.fill, stroke: 'none' };
        rules[`${selectedCell} text`] = { fill: selected.text };

        if (selected.hasShadow) {
            rules[selectedCell] = { filter: SELECTED_SHADOW };
        }
    }

    return rules;
}

const useStyles = createUseStyles({
    diagram: {
        backgroundColor: colors.white,
        '& .can-module rect': {
            strokeWidth: 1,
        },
        '& .can-module text': {
            fill: colors.textDarkPrimary,
            fontSize: 13,
            fontWeight: 600,
        },
        '& .can-module[role="button"]': {
            cursor: 'pointer',
        },
        // The browser rings every focused cell, clicked ones included; only keyboard focus gets a ring.
        '& .can-module[role="button"]:focus': {
            outline: 'none',
        },
        '& .can-module[role="button"]:focus-visible': {
            outline: `2px solid ${colors.blueLink}`,
            outlineOffset: 2,
        },
        // The drawing fills the column, as the design's frame does, and keeps its proportions.
        '& svg': {
            display: 'block',
            height: 'auto',
            width: '100%',
        },
        ...cellRules(),
    },
    tooltip: {
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            padding: '4px 8px',
            textAlign: 'left',
        },
    },
});

/**
 * The CAN Bus diagram of the session: its SVG injected inline, every module cell painted by the status the
 * backend reported, the module's full name as a tooltip on hover and focus, and a click, Enter or Space on
 * a cell with data behind it selecting that module. Every cell is named after its module and status for a
 * screen reader; one without data behind it as an image, not a button. The page decides what a selection opens.
 *
 * @param props - {@link Ssm5CanBusDiagramProps}
 * @returns the diagram
 */
export function Ssm5CanBusDiagram({ modules, onSelect, selectedModuleId, svg }: Ssm5CanBusDiagramProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [tooltip, setTooltip] = useState<DiagramTooltip>();
    const html = useMemo(() => ({ __html: sanitizeDiagramSvg(svg) }), [svg]);
    const statuses = useMemo(() => statusByModuleId(modules), [modules]);

    // The browser would name every cell a second time, from its title element, next to the page's tooltip.
    useEffect(() => {
        if (wrapperRef.current !== null) {
            detachDiagramTitles(wrapperRef.current);
        }
    }, [html]);

    useEffect(() => {
        const root = wrapperRef.current;

        if (root === null) {
            return;
        }

        for (const cell of findDiagramCells(root)) {
            const status = statuses.get(cell.id) ?? UNREPORTED_STATUS;

            cell.element.setAttribute('data-status', status);
            cell.element.setAttribute('data-selected', String(cell.id === selectedModuleId));

            cell.element.setAttribute('aria-label', `${cell.title}, ${t(STATUS_LABEL_KEY[status])}`);

            // A cell without data behind it is named like the others, as an image, but is no button.
            if (isSelectableStatus(status)) {
                cell.element.setAttribute('role', 'button');
                cell.element.setAttribute('tabindex', '0');
            } else {
                cell.element.setAttribute('role', 'img');
                cell.element.removeAttribute('tabindex');
            }
        }
    }, [html, selectedModuleId, statuses, t]);

    const selectCell = useCallback(
        (target: EventTarget | null) => {
            const cell = toDiagramCell(target);

            if (cell !== undefined && isSelectableStatus(statuses.get(cell.id) ?? UNREPORTED_STATUS)) {
                onSelect(cell.id);
            }
        },
        [onSelect, statuses]
    );

    const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => selectCell(event.target), [selectCell]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (SELECT_KEYS.has(event.key)) {
                event.preventDefault();
                selectCell(event.target);
            }
        },
        [selectCell]
    );

    // A new drawing replaces the cells, so a tooltip pointing at one of the old ones goes with them.
    useEffect(() => setTooltip(undefined), [html]);

    const showTooltip = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
        const cell = toDiagramCell(event.target);

        setTooltip((current) => {
            if (cell === undefined) {
                return undefined;
            }

            return current?.target === cell.element ? current : { target: cell.element, text: cell.title };
        });
    }, []);

    const hideTooltip = useCallback(() => setTooltip(undefined), []);

    // Focus leaving a cell hides its name, not the name of another cell the pointer has since moved to.
    const hideTooltipOfBlurred = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
        setTooltip((current) => (current?.target === event.target ? undefined : current));
    }, []);

    const toTooltip = useCallback(
        (injected: OverlayInjectedProps) => (
            <Tooltip className={classes.tooltip} id={TOOLTIP_ID} {...injected}>
                {tooltip?.text}
            </Tooltip>
        ),
        [classes.tooltip, tooltip?.text]
    );

    // The overlay is typed for HTML targets; an SVG cell positions the same way, through its bounding box.
    const tooltipTarget = (tooltip?.target ?? null) as HTMLElement | null;

    return (
        <>
            <div
                className={classes.diagram}
                dangerouslySetInnerHTML={html}
                onBlur={hideTooltipOfBlurred}
                onClick={handleClick}
                onFocus={showTooltip}
                onKeyDown={handleKeyDown}
                onMouseLeave={hideTooltip}
                onMouseOver={showTooltip}
                ref={wrapperRef}
            />
            <Overlay placement="bottom" show={tooltip !== undefined} target={tooltipTarget}>
                {toTooltip}
            </Overlay>
        </>
    );
}
